/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { isValidElement } from "react";

export type ResolverMap = Map<
  string | React.JSXElementConstructor<any>,
  (props: any) => string
>;

/**
 * Traverses any props.children to get their combined text content.
 *
 * https://github.com/lhansford/react-to-text/
 *
 * @param node
 * @param resolvers
 * @returns string
 */
function getTextFromComponent(
  node: React.ReactNode | object,
  resolvers?: ResolverMap
): string {
  if (
    typeof node === "string" ||
    typeof node === "number" ||
    typeof node === "boolean"
  ) {
    return node.toString();
  }
  if (!node) {
    return "";
  }
  if (Array.isArray(node)) {
    return node.map((entry) => getTextFromComponent(entry, resolvers)).join("");
  }

  const [nodeType, nodeProps] = isValidElement(node)
    ? [node.type, node.props]
    : [null, null];
  // check if custom resolver is available
  if (nodeType && resolvers?.has(nodeType)) {
    const resolver = resolvers.get(nodeType)!;
    return resolver(nodeProps);
  }

  // Because ReactNode includes {} in its union we need to jump through a few hoops.
  const props: {
    children?: React.ReactNode;
    dangerouslySetInnerHTML?: { __html: string };
  } = (node as any).props ? (node as any).props : {};

  // Handle dangerouslySetInnerHTML
  if (props?.dangerouslySetInnerHTML?.__html) {
    const html = props.dangerouslySetInnerHTML.__html;
    // Strip HTML tags and decode common HTML entities
    return html
      .replace(/<[^>]*>/g, "") // Remove HTML tags
      .replace(/&nbsp;/g, " ") // Decode non-breaking space
      .replace(/&amp;/g, "&") // Decode ampersand
      .replace(/&lt;/g, "<") // Decode less than
      .replace(/&gt;/g, ">") // Decode greater than
      .replace(/&quot;/g, '"') // Decode quote
      .replace(/&#39;/g, "'") // Decode apostrophe
      .replace(/\n/g, " ") // Remove newlines
      .replace(/&[a-z]+;/gi, ""); // Remove other entities
  }

  if (!props || !props.children) {
    return "";
  }

  return getTextFromComponent(props.children, resolvers);
}

export default getTextFromComponent;
